import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Music2, MapPin, Clock, Mail, Send } from 'lucide-react';
import { contactInfo } from '../../data/mockData';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <footer className="bg-[#2D5016] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_prairie-grains/artifacts/4rxtb62e_Robert%20-Good%20Grains.jpg"
                alt="Good Grains Logo"
                className="w-16 h-16 rounded-full object-cover shadow-lg border-2 border-[#D4A84B]"
                style={{ imageRendering: 'crisp-edges' }}
              />
              <span className="font-serif text-2xl font-bold text-white">
                Good Grains
              </span>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Organic & Non-GMO Animal Feed. Nourishing Montana's farms and homesteads with quality you can trust.
            </p>
            <div className="flex gap-4">
              <a
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4A84B] transition-colors duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4A84B] transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href={contactInfo.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4A84B] transition-colors duration-300"
              >
                <Music2 className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Hours Column */}
          <div>
            <h4 className="font-serif text-xl font-bold mb-6 text-[#D4A84B]">
              Business Hours
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#D4A84B] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white/90">Monday – Saturday</p>
                  <p className="text-white/70 text-sm">9:00 AM – 6:00 PM</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#D4A84B] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white/90">Sunday</p>
                  <p className="text-white/70 text-sm">Closed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Location Column */}
          <div>
            <h4 className="font-serif text-xl font-bold mb-6 text-[#D4A84B]">
              Our Location
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#D4A84B] mt-0.5 flex-shrink-0" />
                <p className="text-white/90 text-sm leading-relaxed">
                  {contactInfo.address}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#D4A84B] mt-0.5 flex-shrink-0" />
                <div>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="text-white/90 hover:text-[#D4A84B] transition-colors text-sm"
                  >
                    {contactInfo.email}
                  </a>
                  <p className="text-white/70 text-sm mt-1">{contactInfo.phone}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Inquiry Column */}
          <div>
            <h4 className="font-serif text-xl font-bold mb-6 text-[#D4A84B]">
              Quick Inquiry
            </h4>
            <p className="text-white/70 text-sm mb-4">
              Subscribe to get updates on new products and animal care tips.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full px-4 py-3 pr-12 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-[#D4A84B] transition-colors text-sm"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md bg-[#D4A84B] flex items-center justify-center hover:bg-[#c49a3d] transition-colors"
                >
                  <Send className="w-4 h-4 text-[#3B2F1E]" />
                </button>
              </div>
              {subscribed && (
                <p className="text-[#D4A84B] text-sm animate-fade-in">
                  Thank you for subscribing!
                </p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm">
              © 2026 by Good Grains LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
